import type { User } from "services/users/model";
import { getTimeStringFromMinutes } from "utils/date";

export const ASSETS_FOLDER_NAME = "assets";
export const EDITOR_FILE_NAME = "editor.json";
export const EXERCISES_FOLDER_ROOT = "exercises";

export function getExercisesRootFolder(sessionUser: User) {
  if (!sessionUser.instructor) {
    throw new Error("The user is not an instructor");
  }

  return `${EXERCISES_FOLDER_ROOT}/${sessionUser.instructor.id}/`;
}

export function getKeyFromQueryString(location: { search?: string }, prefix?: string) {
  if (!location.search) {
    return prefix ?? "";
  }
  return new URLSearchParams(location.search.substring(1)).get("key") ?? "";
}

export function getS3PrefixFromExerciseKey(s3ExerciseKey: string) {
  const parts = s3ExerciseKey.split("/");
  if (parts.length < 2) return "";
  return parts.slice(0, -2).join("/") + "/";
}

export function stripTrailingSlash(key: string) {
  return key.endsWith("/") ? key.slice(0, -1) : key;
}

export function isFolderEditable(key: string) {
  return !key.split("/").includes(ASSETS_FOLDER_NAME);
}

/**
 * Minutes left in a course, or null when nothing says how long the remaining
 * lessons are. A course whose lessons all omit a length is not the same as one
 * with nothing left to do, and the difference is visible: the caller shows an
 * estimate for the second and nothing for the first.
 */
export function remainingCourseMinutes(
  remaining: ReadonlyArray<{ length?: number | null }>
): number | null {
  if (
    remaining.length > 0 &&
    remaining.every(lesson => typeof lesson.length !== "number")
  ) {
    return null;
  }
  return remaining.reduce((total, lesson) => total + (lesson.length ?? 0), 0);
}

/** What a course card shows for time left, or nothing when there is no estimate. */
export function courseTimeLeft(
  progress: { remaining: ReadonlyArray<{ length?: number | null }> } | null | undefined
) {
  if (!progress) {
    return "";
  }
  const minutes = remainingCourseMinutes(progress.remaining);
  return minutes === null ? "" : getTimeStringFromMinutes(minutes);
}
